import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { useAssignPermissionsToRoleMutation, useGetPermissionsQuery, useGetPermissionSetsQuery } from '../../../services/SkillsManagement';
import { useDispatch } from 'react-redux';
import { ASSIGN_PERMISSIONS_TO_ROLE } from '../../../Reducers/Actions';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import SecurityIcon from '@mui/icons-material/Security';
import { 
    MenuItem, 
    ListItemIcon, 
    ListItemText, 
    Grid,
    FormGroup,
    FormControlLabel,
    Checkbox,
    Typography,
    Box,
    TextField,
    InputAdornment,
    TableContainer,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    useTheme,
    Paper,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Divider
} from '@mui/material';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import LoaderSpinner from '../../../SystemDesign/LoaderSpinner';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { tokens } from '../../../theme';
import TextFieldComp from '../../../SystemDesign/TextFieldComp';
import { translatePermissionSetName, translateModuleName, translatePermissionAction } from '../../../Utils/PermissionDictionary';

// Create a separate memoized accordion content component to improve performance
const PermissionSetAccordionContent = React.memo(({ permissionSet, selectedPermissions, permissionsByModel, permissionTypes, handlePermissionToggle, getModelsByCategory }) => {
    // Only process data when component renders (when accordion is expanded)
    const modelsByCategory = useMemo(() => 
        getModelsByCategory(permissionSet.models), 
        [permissionSet.models, getModelsByCategory]
    );

    return (
        <AccordionDetails sx={{ pt: 0, pb: 2 }}>
            <Divider sx={{ mb: 2 }} />
            {Object.entries(modelsByCategory).map(([category, models]) => (
                <Box key={`${permissionSet.name}-${category}`} sx={{ mb: 3 }}>
                    <Typography 
                        variant="subtitle2" 
                        color="primary" 
                        sx={{ 
                            mb: 2,
                            fontWeight: 'bold',
                            backgroundColor: 'rgba(0, 0, 0, 0.03)',
                            p: 1,
                            borderRadius: 1
                        }}
                    >
                        {category} ({models.length} modules)
                    </Typography>
                    
                    <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Module</TableCell>
                                    {permissionTypes.map(type => (
                                        <TableCell key={type} align="center" sx={{ fontWeight: 'bold' }}>
                                            {translatePermissionAction(type)}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {models.map((modelData) => {
                                    const translatedModuleName = translateModuleName(modelData.model);
                                    return (
                                        <TableRow
                                            key={`${permissionSet.name}-${modelData.model}`}
                                            sx={{ '&:nth-of-type(odd)': { backgroundColor: 'action.hover' } }}
                                        >
                                            <TableCell component="th" scope="row" sx={{ wordBreak: 'break-word', whiteSpace: 'normal' }}>
                                                {translatedModuleName}
                                            </TableCell>
                                            {permissionTypes.map(type => (
                                                <TableCell key={`${modelData.model}-${type}`} align="center" padding="checkbox">
                                                    {modelData.actions[type] ? (
                                                        <Checkbox
                                                            checked={selectedPermissions.includes(modelData.actions[type])}
                                                            onChange={() => handlePermissionToggle(modelData.actions[type])}
                                                            size="small"
                                                        />
                                                    ) : permissionsByModel[modelData.model] && permissionsByModel[modelData.model][type] ? (
                                                        <Checkbox
                                                            checked={selectedPermissions.includes(permissionsByModel[modelData.model][type])}
                                                            onChange={() => handlePermissionToggle(permissionsByModel[modelData.model][type])}
                                                            size="small"
                                                        />
                                                    ) : (
                                                        <Checkbox
                                                            disabled
                                                            size="small"
                                                        />
                                                    )}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            ))}
        </AccordionDetails>
    );
});

const AssignPermissionsToRole = ({ row }) => {
    // Theme configuration
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);

    // State management
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [close, setClose] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedPermissionSet, setExpandedPermissionSet] = useState(null);
    const [permissionsByModel, setPermissionsByModel] = useState({});

    // Context and hooks
    const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
    const [assignPermissions, { isLoading: isAssigning }] = useAssignPermissionsToRoleMutation();
    const dispatch = useDispatch();
    const { data: permissionsData, isLoading: permissionsLoading } = useGetPermissionsQuery({
        filters: "",
        pagination: "",
        sorting: "",
    });
    const { data: permissionSetsData, isLoading: permissionSetsLoading } = useGetPermissionSetsQuery();

    // Initialize selected permissions - optimized to run just once
    useEffect(() => {
        if (row.original.permissions) {
            setSelectedPermissions(row.original.permissions.map(p => p.permission_id));
        }
    }, [row.original.permissions]);

    // Group permissions by model when data is loaded - optimized with useMemo
    useEffect(() => {
        if (permissionsData?.permissions) {
            const modelMap = {};
            permissionsData.permissions.forEach(permission => {
                if (!modelMap[permission.model]) {
                    modelMap[permission.model] = {};
                }
                modelMap[permission.model][permission.action] = permission.permission_id;
            });

            setPermissionsByModel(modelMap);
        }
    }, [permissionsData]);

    // Optimize permission toggle with useCallback to prevent recreation on each render
    const handlePermissionToggle = useCallback((permissionId) => {
        setSelectedPermissions(prev => {
            // Check if ID is already in the array before manipulating it
            const exists = prev.includes(permissionId);
            if (exists) {
                return prev.filter(id => id !== permissionId);
            } else {
                return [...prev, permissionId];
            }
        });
    }, []);

    const handleSubmit = useCallback(async () => {
        try {
            const response = await assignPermissions({ 
                roleId: row.original.roleId,
                body: { permissionIds: selectedPermissions }
            }).unwrap();
            
            dispatch({ 
                type: ASSIGN_PERMISSIONS_TO_ROLE, 
                payload: { roleId: row.original.roleId, permissions: response.role.permissions }
            });
            setClose(true);
            setInfoAlert({ 
                severity: 'success', 
                message: `Permissions modified successfully for role ${row.original.name}` 
            });
            setOpenSnackBar(true);
        } catch (error) {
            setInfoAlert({ 
                severity: 'error', 
                message: error.data?.message || 'An error occurred while modifying permissions' 
            });
            setOpenSnackBar(true);
        }
    }, [assignPermissions, row.original.roleId, selectedPermissions, dispatch, row.original.name, setInfoAlert, setOpenSnackBar]);

    // Define model categories and their members - memoized to prevent recreation
    const modelCategories = useMemo(() => ({
        "Users and Access": [
            "User", "UserProfile", "Role", "Permission", "RolePermission", "UserPermission"
        ],
        "Company Structure": [
            "Company", "Poste"
        ],
        "Recruitment": [
            "Candidate", "Application", "ApplicationNote", "JobOffer", "JobPreferences", "Certificate", "CertificationType"
        ],
        "Training": [
            "Course", "CourseSection", "CourseSession", "CourseCategory", "CourseCategoryMapping", "SessionBooking", "SessionInstructor", "Enrollment", "Instructor"
        ],
        "Competences": [
            "Departement", "Unit", "Skill", "SkillCategoryMapping", "Evaluation"
        ],
        "Quiz": [
            "Quiz", "UserQuizAttempt", "UserQuizAssignment", "UserQuizResponse"
        ],
        "History and Notifications": [
            "History", "Notification"
        ]
    }), []);

    // Group permissions by permission sets and categories - memoized based on dependencies
    const processedPermissionSets = useMemo(() => {
        if (permissionsLoading || permissionSetsLoading || 
            !permissionsData?.permissions || !permissionSetsData?.permissionSets) {
            return [];
        }

        // Search term normalization (remove accents, lowercase)
        const normalizeString = (str) => str
            .toLowerCase()
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, ""); // Remove diacritics (accents)
        
        const normalizedSearchTerm = normalizeString(searchTerm);

        // Map permission sets
        return permissionSetsData.permissionSets
            .filter(set => {
                // Exclude specific permission sets by name
                const excludedNames = [
                    'recruitment_management_permissions',
                    'training_management_permissions'
                ];
                if (excludedNames.includes(set.name)) return false;

                if (!set.modelActions) return false;

                const normalizeString = (str) => str
                    .toLowerCase()
                    .normalize("NFD")
                    .replace(/[\u0300-\u036f]/g, "");

                const normalizedSearchTerm = normalizeString(searchTerm);

                // Keep sets whose models match the search term
                return Object.keys(set.modelActions).some(model => {
                    const englishMatch = normalizeString(model).includes(normalizedSearchTerm);
                    const frenchTranslation = translateModuleName(model);
                    const frenchMatch = normalizeString(frenchTranslation).includes(normalizedSearchTerm);
                    return englishMatch || frenchMatch;
                });
            })
            .map(set => {
                // For each permission set, create a models array with only the models
                // that are explicitly mentioned in the set's modelActions
                const setModels = [];
                
                if (set.modelActions) {
                    Object.entries(set.modelActions).forEach(([model, actions]) => {
                        // Skip if neither English model name nor French translation matches search
                        const englishMatch = normalizeString(model).includes(normalizedSearchTerm);
                        const frenchTranslation = translateModuleName(model);
                        const frenchMatch = normalizeString(frenchTranslation).includes(normalizedSearchTerm);
                        
                        if (!englishMatch && !frenchMatch && normalizedSearchTerm !== '') {
                            return;
                        }
                        
                        // Special handling for Permission model - create separate entries for both permission types
                        if (model === 'Permission') {
                            // Handle Userpermission
                            if (permissionsByModel['Userpermission']) {
                                const userPermissions = {};
                                actions.forEach(action => {
                                    if (permissionsByModel['Userpermission'][action]) {
                                        userPermissions[action] = permissionsByModel['Userpermission'][action];
                                    }
                                });
                                
                                if (Object.keys(userPermissions).length > 0) {
                                    setModels.push({
                                        model: 'Userpermission', // Distinct display name
                                        actions: userPermissions
                                    });
                                }
                            }
                            
                            // Handle Rolepermission
                            if (permissionsByModel['Rolepermission']) {
                                const rolePermissions = {};
                                actions.forEach(action => {
                                    if (permissionsByModel['Rolepermission'][action]) {
                                        rolePermissions[action] = permissionsByModel['Rolepermission'][action];
                                    }
                                });
                                
                                if (Object.keys(rolePermissions).length > 0) {
                                    setModels.push({
                                        model: 'Rolepermission', // Distinct display name
                                        actions: rolePermissions
                                    });
                                }
                            }
                        }
                        // Standard handling for other models
                        else {
                            // First check for direct mapping
                            let modelToCheck = model;
                            
                            // Map actions to permission IDs
                            const modelPermissions = {};
                            actions.forEach(action => {
                                if (permissionsByModel[modelToCheck] && permissionsByModel[modelToCheck][action]) {
                                    modelPermissions[action] = permissionsByModel[modelToCheck][action];
                                }
                            });
                            
                            // Only add if there are permissions for this model
                            if (Object.keys(modelPermissions).length > 0) {
                                setModels.push({
                                    model, // Keep original model name for display
                                    actions: modelPermissions
                                });
                            }
                        }
                    });
                }
                
                return {
                    name: set.name,
                    description: translatePermissionSetName(set.description || set.name),
                    models: setModels.sort((a, b) => a.model.localeCompare(b.model))
                };
            })
            .filter(set => set.models.length > 0) // Only keep sets with models
            .sort((a, b) => a.name.localeCompare(b.name)); // Sort sets by name
    }, [permissionsLoading, permissionSetsLoading, permissionsData, permissionSetsData, permissionsByModel, searchTerm]);

    // Get category for a model - memoized function
    const getModelCategory = useCallback((model) => {
        for (const [category, models] of Object.entries(modelCategories)) {
            if (models.includes(model)) {
                return category;
            }
        }
        return "Autres";
    }, [modelCategories]);

    // Group models by categories - memoized function
    const getModelsByCategory = useCallback((models) => {
        const categories = {};
        models.forEach(modelData => {
            const category = getModelCategory(modelData.model);
            if (!categories[category]) {
                categories[category] = [];
            }
            categories[category].push(modelData);
        });
        return categories;
    }, [getModelCategory]);

    // Constants - memoized to prevent recreation
    const permissionTypes = useMemo(() => ['read', 'update', 'delete', 'create', 'manage'], []);
    
    // Optimize accordion change handler with useCallback
    const handleAccordionChange = useCallback((permissionSetName) => (event, isExpanded) => {
        setExpandedPermissionSet(isExpanded ? permissionSetName : null);
    }, []);

    // Pre-translate common items to avoid translation in render loop
    const translatedPermissionTypes = useMemo(() => {
        return permissionTypes.map(type => ({
            type,
            translation: translatePermissionAction(type)
        }));
    }, [permissionTypes]);

    return (
        <Box sx={{ display: 'inline-block' }}>
            <MuiDialog
                icon={<SecurityIcon />}
                triggerButton={
                    <MenuItem>
                        <ListItemIcon>
                            <SecurityIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Assign permissions</ListItemText>
                    </MenuItem>
                }
                title="Assign permissions"
                subtitle={`Permissions of role ${row.original.name}`}
                content={
                    <Box sx={{ mt: 2, width: 'auto', minWidth: '100%' }}>
                        {permissionsLoading || permissionSetsLoading ? (
                            <LoaderSpinner loading={permissionsLoading || permissionSetsLoading} />
                        ) : (
                            <>
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    mb: 2 
                                }}>
                                    <Typography 
                                        variant="subtitle1" 
                                        color="primary"
                                        sx={{ 
                                            fontWeight: 'medium',
                                            backgroundColor: colors.secondary[50],
                                            color: colors.secondary[800],
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: 1,
                                        }}
                                    >
                                        {selectedPermissions.length} permission(s) sélectionnée(s)
                                    </Typography>
                                </Box>
                                                                
                                {/* Informative box explaining permission actions */}
                                <Box
                                    sx={{
                                        mb: 2,
                                        p: 1.5,
                                        borderRadius: 1,
                                        border: '1px dashed',
                                        borderColor: theme.palette.divider,
                                        backgroundColor: theme.palette.background.paper,
                                    }}
                                >
                                    <Box sx={{ 
                                        display: 'flex', 
                                        alignItems: 'center',
                                        mb: 1,
                                        pb: 1,
                                        borderBottom: '1px solid',
                                        borderColor: theme.palette.divider
                                    }}>
                                        <SecurityIcon 
                                            color="primary" 
                                            fontSize="small" 
                                            sx={{ mr: 1 }} 
                                        />
                                        <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                            Legend of permissions
                                        </Typography>
                                    </Box>
                                    
                                    <Grid container spacing={1}>
                                        {[
                                            { 
                                                type: 'read', 
                                                description: 'Read', 
                                                details: 'Read only',
                                                icon: '👁️',
                                            },
                                            { 
                                                type: 'create', 
                                                description: 'Create', 
                                                details: 'Add entries',
                                                icon: '➕',
                                            },
                                            { 
                                                type: 'update', 
                                                description: 'Update', 
                                                details: 'Change data',
                                                icon: '✏️',
                                            },
                                            { 
                                                type: 'delete', 
                                                description: 'Delete', 
                                                details: 'Delete data',
                                                icon: '🗑️',
                                            },
                                            { 
                                                type: 'manage', 
                                                description: 'Manage', 
                                                details: 'Full access',
                                                icon: '⚙️',
                                            }
                                        ].map(({ type, description, details, icon, bgcolor }) => (
                                            <Grid item size={{ xs: 6, md: 4, lg: 2.4 }} key={type}>
                                                <Box 
                                                    sx={{ 
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        borderRadius: 1,
                                                        p: 1,
                                                        backgroundColor: colors.grey[100],
                                                        height: '40px',
                                                        overflow: 'hidden'
                                                    }}
                                                >
                                                    <Box 
                                                        sx={{ 
                                                            mr: 1, 
                                                            fontSize: '1rem',
                                                            flexShrink: 0,
                                                            width: 20,
                                                            textAlign: 'center'
                                                        }}
                                                    >
                                                        {icon}
                                                    </Box>
                                                    <Box sx={{ 
                                                        display: 'flex', 
                                                        flexDirection: 'column',
                                                        justifyContent: 'center',
                                                        height: '100%'
                                                    }}>
                                                        <Typography 
                                                            variant="caption" 
                                                            sx={{ 
                                                                fontWeight: 'bold',
                                                                lineHeight: 1.2
                                                            }}
                                                        >
                                                            {description}
                                                        </Typography>
                                                        <Typography 
                                                            variant="caption" 
                                                            color="text.secondary"
                                                            sx={{ lineHeight: 1.2 }}
                                                        >
                                                            {details}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        ))}
                                    </Grid>
                                </Box>
                                <TextFieldComp
                                    variant="outlined"
                                    size="small"
                                    placeholder="Filter by module..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    sx={{ mb: 2, width: '100%' }}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                                
                                {/* Permission sets accordions */}
                                {processedPermissionSets.map((permissionSet) => (
                                    <Accordion 
                                        key={permissionSet.name}
                                        expanded={expandedPermissionSet === permissionSet.name}
                                        onChange={handleAccordionChange(permissionSet.name)}
                                        TransitionProps={{ unmountOnExit: true }}
                                        sx={{ 
                                            mb: 2,
                                            '&:before': {
                                                display: 'none',
                                            },
                                            boxShadow: 'none',
                                            border: '1px solid #e0e0e0',
                                        }}
                                    >
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{ 
                                                fontWeight: 'bold',
                                                minHeight: '48px',
                                                '& .MuiAccordionSummary-content': {
                                                    margin: '8px 0',
                                                }
                                            }}
                                        >
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {permissionSet.description}
                                                <Typography 
                                                    component="span" 
                                                    variant="body2" 
                                                    sx={{ ml: 1, color: 'text.secondary' }}
                                                >
                                                    ({permissionSet.models.length} modules)
                                                </Typography>
                                            </Typography>
                                        </AccordionSummary>
                                        {/* Only render content when accordion is expanded */}
                                        {expandedPermissionSet === permissionSet.name && (
                                            <PermissionSetAccordionContent
                                                permissionSet={permissionSet}
                                                selectedPermissions={selectedPermissions}
                                                permissionsByModel={permissionsByModel}
                                                permissionTypes={permissionTypes}
                                                handlePermissionToggle={handlePermissionToggle}
                                                getModelsByCategory={getModelsByCategory}
                                            />
                                        )}
                                    </Accordion>
                                ))}
                            </>
                        )}
                    </Box>
                }
                existAction={true}
                actionTitle="Save"
                actionFunction={handleSubmit}
                isLoading={isAssigning}
                onClose={close}
                maxWidth="md"
            />
        </Box>
    );
};

export default React.memo(AssignPermissionsToRole);
